import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

/**
 * Declaración de los filtros de la grilla de Tickets para <GridFilterPanel />.
 *
 * Vive en su propio módulo porque lo consumen DOS lados: el panel (para renderizar) y
 * TicketsGrid (para calcular el query por defecto con las mismas reglas de serialización).
 *
 * VEC-3698 fase 1: migración al panel compartido, conservando el tipo de control de cada campo.
 * VEC-3698 fase 2: los 17 desplegables simples pasaron a `multiselect`.
 *
 * El query NO cambió de forma: un multiselect con un solo valor serializa igual que el select
 * —`estado=PENDIENTES`—, y del lado del backend cada filtro parsea la lista con
 * `GridFilterHelper::separar()`, que trata un valor simple como una lista de uno. Eso es lo que
 * mantiene funcionando a los consumidores que NO son esta grilla: integraciones y clientes
 * externos siguen mandando un valor por filtro y obtienen lo mismo que antes.
 */

export const TICKETS_FILTROS_STORAGE_KEY = 'TicketsGrid_filtros';

/** La key vieja, con shape `{activo, filtros}`. Ver `migrarFiltrosPersistidos`. */
const STORAGE_KEY_LEGACY = 'TicketsGridFilterNew';

/**
 * Migra la persistencia previa a VEC-3698, una sola vez por usuario.
 *
 * Antes se guardaba `{activo: bool, filtros: {...}}` bajo otra key: `activo` era el switch
 * "Persistir Filtros", que ya no existe (la persistencia es siempre, regla del patrón). El
 * panel espera el estado plano, así que se sube un nivel el contenido de `filtros`. Sin
 * esto, cada usuario perdería los filtros que tenía guardados.
 *
 * Se llama antes de montar el panel, porque el panel lee localStorage en su estado inicial.
 */
export const migrarFiltrosPersistidos = () => {
	try {
		if (window.localStorage.getItem(TICKETS_FILTROS_STORAGE_KEY)) return;

		const viejo = window.localStorage.getItem(STORAGE_KEY_LEGACY);
		if (!viejo) return;

		const parseado = JSON.parse(viejo);
		if (parseado && parseado.filtros) {
			window.localStorage.setItem(TICKETS_FILTROS_STORAGE_KEY, JSON.stringify(parseado.filtros));
		}
		window.localStorage.removeItem(STORAGE_KEY_LEGACY);
	} catch (e) {}
};

/**
 * Valores por defecto: el listado abre con los tickets PENDIENTES y, donde hay gestoría,
 * con el tipo POR DEFECTO (que el backend traduce a "todo lo que no es GESTORIA").
 * Es una función y no una constante porque los labels salen de intl.
 *
 * Sin gestoría el tipo queda vacío, igual que hoy: `DEFAULT` filtra `tipo != 'GESTORIA'`
 * (TicketGridNew::scopeUserSelectedFilters) y sin gestoría no hay tickets de ese tipo, así
 * que vacío y DEFAULT devuelven lo mismo.
 */
export const getTicketsFilterDefaults = (intl) => {
	// Los valores van en array porque los campos son `multiselect` (VEC-3698 fase 2). Un solo
	// elemento serializa igual que antes —`estado=PENDIENTES`—, así que el query por defecto no
	// cambió: lo que cambió es que ahora se le pueden sumar valores.
	const defaults = {
		estado: [{
			value: 'PENDIENTES',
			label: intl.formatMessage({ id: 'ticketsGridFilters.select_pendientes', defaultMessage: 'PENDIENTES' }),
		}],
	};

	if (ConfigBusiness.get('mantenimientos.gestoria.habilitado') === 'true') {
		defaults.ticketTipo = [{
			value: 'DEFAULT',
			label: intl.formatMessage({ id: 'ticketsGridFilters.select_por_defecto', defaultMessage: 'POR DEFECTO' }),
		}];
	}

	return defaults;
};

const ANCHO_CHICO = { xs: 12, sm: 6, md: 2, lg: 2 };
const ANCHO_MEDIO = { xs: 12, sm: 6, md: 3, lg: 3 };
const ANCHO_MINIMO = { xs: 12, sm: 4, md: 1, lg: 1 };

/**
 * @param columnsEnabled  los flags de config que ya calcula TicketsGrid::configColumns
 * @param entidades       callbacks del filtro de Entidad: { onSearch, onResolve }
 *
 * Sobre `visible`: en el panel viejo el gateo estaba escrito como
 * `{...!columnsEnabled.pais && { display: "none" }}`, que pasa `display` como PROP de Grid
 * y en MUI v4 no hace nada: los campos gateados se venían mostrando siempre. Acá el gateo
 * pasa a ser efectivo, que es lo que el código pretendía y lo que ya hacía el único campo
 * escrito con `style` (origen_checklist). Es el único cambio observable de esta migración.
 */
export const buildTicketsFilterFields = (columnsEnabled, entidades) => {
	const columnas = columnsEnabled || {};
	const callbacksEntidades = entidades || {};

	return [
		{
			name: 'ticketTipo',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.tipo_label',
			label: 'Tipo',
			size: ANCHO_MEDIO,
		},
		{
			name: 'id',
			type: 'text',
			messageId: 'ticketsGridFiltersFilters.render.id_label',
			label: 'Tkt',
			size: ANCHO_MINIMO,
		},
		{
			name: 'estado',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.estado_label',
			label: 'Estado',
			size: ANCHO_MEDIO,
		},
		{
			name: 'fechaDesde',
			type: 'date',
			messageId: 'ticketsGridFiltersFilters.render.fecha_desde_label',
			label: 'Fecha Desde',
			size: ANCHO_CHICO,
		},
		{
			name: 'fechaHasta',
			type: 'date',
			messageId: 'ticketsGridFiltersFilters.render.fecha_hasta_label',
			label: 'Fecha Hasta',
			size: ANCHO_CHICO,
		},
		{
			name: 'fechaEnTaller',
			type: 'date',
			messageId: 'ticketsGridFiltersFilters.render.fecha_en_taller_label',
			label: 'Fecha en Taller',
			size: ANCHO_CHICO,
			visible: columnas.fechaEnTaller === true,
		},
		{
			name: 'nivelAprobacionPresupuesto',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.nivelesDeAprobacion_label',
			label: 'Nivel Aprobacion',
			size: ANCHO_MEDIO,
			visible: columnas.nivelAprobacionPresupuesto === true,
		},
		{
			name: 'detalle',
			type: 'text',
			messageId: 'ticketsGridFiltersFilters.render.detalle_label',
			label: 'Detalle',
			size: ANCHO_MEDIO,
		},
		{
			name: 'servicio',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.servicio_label',
			label: 'Servicio',
			size: ANCHO_MEDIO,
		},
		{
			name: 'prioridad',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.label_prioridad',
			label: 'Prioridad',
			size: ANCHO_MEDIO,
			visible: columnas.prioridad === true,
		},
		{
			// VEC-3555: los chips resueltos viajan en `entidades` tipados
			// ("Moviles:12,Llantas:5") y lo que quedó sin resolver en `entidad`, como
			// búsqueda parcial. Las opciones no se precargan: se buscan en el servidor.
			name: 'entidades',
			textName: 'entidad',
			type: 'pastableMultiselect',
			messageId: 'ticketsGridFiltersFilters.render.entidad_label',
			label: 'Entidad',
			size: ANCHO_MEDIO,
			onSearch: callbacksEntidades.onSearch,
			onResolve: callbacksEntidades.onResolve,
		},
		{
			name: 'vin',
			type: 'text',
			messageId: 'ticketsGridFiltersFilters.render.vin_label',
			label: 'VIN',
			size: ANCHO_MEDIO,
		},
		{
			name: 'gerenciador',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.proveedor_label',
			label: 'Proveedor',
			size: ANCHO_MEDIO,
		},
		{
			name: 'region',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.region_label',
			label: 'Region',
			size: ANCHO_MEDIO,
			visible: columnas.region === true,
		},
		{
			name: 'subRegion',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_subregion.label',
			label: 'Sub-Región',
			size: ANCHO_MEDIO,
		},
		{
			name: 'base',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.base_label',
			label: 'Base',
			size: ANCHO_MEDIO,
		},
		{
			name: 'pais',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.pais_label',
			label: 'Pais',
			size: ANCHO_MEDIO,
			visible: columnas.pais === true,
		},
		{
			name: 'centroCostos',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.ceco_label',
			label: 'Centro Costos',
			size: ANCHO_MEDIO,
			visible: columnas.centroCostos === true,
		},
		{
			name: 'centroBeneficios',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.cebe_label',
			label: 'Centro Beneficios',
			size: ANCHO_MEDIO,
			visible: columnas.centroBeneficios === true,
		},
		{
			name: 'responsable1',
			type: 'text',
			messageId: 'ticketsGridFiltersFilters.render.responsable_label',
			label: 'Responsable',
			size: ANCHO_MEDIO,
			visible: columnas.responsable1 === true,
		},
		{
			name: 'enTaller',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.taller_label',
			label: 'Taller',
			size: ANCHO_MEDIO,
		},
		{
			// El período no viaja como tal: se serializa a turnoInicio / turnoFinal.
			name: 'turno',
			type: 'periodo',
			startName: 'turnoInicio',
			endName: 'turnoFinal',
			messageId: 'Fecha_de_turno',
			label: 'Fecha de turno',
			size: ANCHO_MEDIO,
			visible: columnas.fechaHoraTurno === true,
		},
		{
			name: 'fechaPrometida',
			type: 'periodo',
			startName: 'fechaPrometidaInicio',
			endName: 'fechaPrometidaFinal',
			messageId: 'Fecha_Prometida',
			label: 'Fecha Prometida',
			size: ANCHO_MEDIO,
			visible: columnas.fechaPrometida === true,
		},
		{
			name: 'mttTotal',
			type: 'text',
			messageId: 'ticketsGridFiltersFilters.render.tt_label',
			label: 'TT',
			size: ANCHO_CHICO,
		},
		{
			name: 'mttParcial',
			type: 'text',
			messageId: 'ticketsGridFiltersFilters.render.tp_label',
			label: 'TP',
			size: ANCHO_CHICO,
		},
		{
			name: 'encuestaNivelSatisfaccion',
			type: 'text',
			messageId: 'ticketsGridFiltersFilters.render.encuestaSatisfaccion_label',
			label: 'Satisfaccion',
			size: ANCHO_CHICO,
			visible: columnas.encuestaSatisfaccion === true,
		},
		{
			name: 'fechaRealizado',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.realizado_label',
			label: 'Realizado',
			size: ANCHO_CHICO,
		},
		{
			name: 'abono',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.abono_label',
			label: 'Abono',
			size: ANCHO_CHICO,
			visible: columnas.abono === true,
		},
		{
			name: 'turnoAsignado',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.turno_asignado_label',
			label: 'Turno asignado',
			size: ANCHO_CHICO,
			visible: columnas.turnoAsignado === true,
		},
		{
			name: 'tipoModelo',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.tipoModelo_label',
			label: 'Tipo Modelo',
			size: ANCHO_MEDIO,
			visible: columnas.tipoModelo === true,
		},
		{
			name: 'origen',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.origen',
			label: 'Origen ticket',
			size: ANCHO_MEDIO,
			visible: columnas.origen_checklist === true,
		},
		{
			name: 'transportadora',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_transportadora.label',
			label: 'Transportadora',
			size: ANCHO_MEDIO,
			visible: columnas.transportadora === true,
		},
		{
			name: 'clientExternalTicketId',
			type: 'text',
			messageId: 'ticketsGridFilters.render.clientExternalTicketId_label',
			label: 'OS',
			size: ANCHO_MEDIO,
			visible: columnas.clientExternalTicketId === true,
		},
		{
			name: 'rentadora',
			type: 'multiselect',
			messageId: 'combustiblesGrid.render.filtros_rentadora.label',
			label: 'Rentadora',
			size: ANCHO_MEDIO,
			visible: columnas.rentadora === true,
		},
		{
			name: 'unidad',
			type: 'text',
			messageId: 'ticketsGridFiltersFilters.render.unidad_label',
			label: 'Unidad',
			size: ANCHO_MEDIO,
		},
		{
			name: 'usuarioCreacion',
			type: 'multiselect',
			messageId: 'ticketsGridFilters.render.usuario_creacion_label',
			label: 'Usuario de creación',
			size: ANCHO_MEDIO,
		},
	];
};
